import { initFederation } from '@angular-architects/native-federation';
import './zone-fork-registry'; // Must run after zone.js (loaded as polyfill) and before any Angular bootstrap
import { environment } from './environments/environment';

// Reflect the build-time platform onto <html> BEFORE Angular paints, so CSS can gate
// native-feel styles via [data-platform="native"] with no flash of web layout.
// environment.platform is a compile-time constant (set by fileReplacements: web builds
// use environment(.prod).ts → 'web'; the native build config uses environment.native.ts →
// 'native'). This is the permanent home for the attribute (replaces the interim assignment
// that lived in bootstrap.ts during Phase 1).
document.documentElement.dataset.platform = environment.platform;

// Native only: prevent outer-viewport rubber-band scroll so only the scrollable
// content region scrolls. On web (including phone-sized browsers) the browser
// manages its own scrolling — leave it be.
if (environment.platform === 'native') {
  // Prevent outer-viewport rubber-band bounce while allowing every genuinely
  // scrollable region to scroll (DXTGATEW-12709).
  //
  // The previous implementation only allowed a hardcoded allowlist
  // (#content / #native-content / ion-content) and called preventDefault() on
  // everything else — which silently killed swipe-scroll for ANY other
  // scrollable element (e.g. an injected debug console, or a federated MFE's
  // scroll container that isn't one of those exact elements). We now allow the
  // gesture whenever the touch path contains a genuinely scrollable element,
  // and only block when it's over nothing scrollable (true viewport bounce).
  const isScrollable = (el: unknown): boolean => {
    if (!(el instanceof HTMLElement)) return false;
    const style = getComputedStyle(el);
    const oy = style.overflowY;
    const canScrollY = (oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight;
    const ox = style.overflowX;
    const canScrollX = (ox === 'auto' || ox === 'scroll') && el.scrollWidth > el.clientWidth;
    return canScrollY || canScrollX;
  };

  document.addEventListener(
    'touchmove',
    (e) => {
      const path = e.composedPath() as HTMLElement[];
      const inScrollable = path.some(
        (el) =>
          // Fast-path: the known shell scroll containers + any Ionic ion-content
          // (its shadow .inner-scroll owns the scroll; the host is in composedPath).
          (el as HTMLElement)?.id === 'content' ||
          (el as HTMLElement)?.id === 'native-content' ||
          (typeof (el as HTMLElement)?.tagName === 'string' &&
            (el as HTMLElement).tagName.toLowerCase() === 'ion-content') ||
          // General case: any element that can actually scroll.
          isScrollable(el),
      );
      if (!inScrollable) {
        e.preventDefault();
      }
    },
    { passive: false },
  );
}

// Native federation resolves ./remoteEntry.json and import map entries relative to <base href>.
// It requires a trailing slash (e.g. /app-isso/) but the original base-path has no trailing slash.
// Add it here before initFederation runs. Angular's APP_BASE_HREF is set separately in bootstrap
// via computeBaseHref which strips the trailing slash for correct router path matching.
const baseEl = document.querySelector('base');
const originalBase = baseEl?.getAttribute('href') || '/';
if (baseEl && !originalBase.endsWith('/')) {
  baseEl.setAttribute('href', originalBase + '/');
}
const options = environment.production ? { cacheTag: 'remoteEntry.20261029T143501.json' } : {};
console.log({ options });
initFederation({}, options)
  .catch((err) => console.error(err))
  .then((_) => import('./bootstrap').then((m) => m.initAppShell()))
  .catch((err) => console.error(err));
